/**
 * Error Boundary Component
 * Catches and suppresses script errors that occur from external scripts in HTML templates
 */

import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Suppress generic "Script error" messages
    if (error && (error.message === 'Script error.' || error.message === 'Script error')) {
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Suppress script errors - they're often harmless and related to external scripts
    const errorMessage = error?.message || error?.toString() || '';
    if (errorMessage.includes('Script error') || errorMessage === 'Script error.' || errorMessage === 'Script error') {
      console.warn('Suppressed script error (likely from external scripts in template):', error);
      this.setState({ hasError: false, error: null });
      // Prevent error from propagating to React's error overlay
      return;
    }

    // Log other errors for debugging
    if (this.state.hasError) {
      console.error('Error caught by boundary:', error, errorInfo);
    }
  }

  render() {
    // If we have a real error (not a script error), show error UI
    if (this.state.hasError && this.state.error) {
      return (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
            Something went wrong
          </h3>
          <p className="text-sm text-red-600 dark:text-red-400">
            {this.state.error.message || 'An unexpected error occurred'}
          </p>
        </div>
      );
    }

    // Otherwise, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;

